import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useAccount } from '../contexts/AccountContext';
import { AccountType } from '../types';
import { PrimaryButton } from './buttons';

const AccountSetup: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { createAccount, refreshAccounts } = useAccount();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedType, setSelectedType] = useState<AccountType | null>(null);
  const [accountName, setAccountName] = useState('');
  const [ipiNumber, setIpiNumber] = useState('');
  const [pro, setPro] = useState('');

  const handleSelectType = (type: AccountType) => {
    setSelectedType(type);
    // Set default name based on type
    if (type === 'artist') {
      setAccountName(user?.name || '');
    } else {
      setAccountName(`${user?.name || 'My'} Management`);
    }
  };

  const handleCreateAccount = async () => {
    if (!selectedType || !accountName.trim()) {
      setError('Please enter an account name');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      await createAccount({
        name: accountName.trim(),
        type: selectedType,
        ...(selectedType === 'artist' && {
          ipiNumber: ipiNumber.trim() || undefined,
          pro: pro.trim() || undefined,
        }),
      });

      // Refresh accounts to ensure state is up to date
      await refreshAccounts();

      // Navigate to appropriate dashboard based on account type
      if (selectedType === 'manager') {
        navigate('/manager-dashboard');
      } else {
        navigate('/my-splits');
      }
    } catch (err) {
      console.error('Failed to create account:', err);
      setError(err instanceof Error ? err.message : 'Failed to create account');
    } finally {
      setIsLoading(false);
    }
  };

  const handleBack = () => {
    setSelectedType(null);
    setAccountName('');
    setIpiNumber('');
    setPro('');
    setError(null);
  };

  // Step 2: Enter account name
  if (selectedType) {
    return (
      <div className="min-h-screen bg-gradient-secondary flex items-center justify-center px-4">
        <div className="max-w-md w-full">
          {/* Logo */}
          <div className="text-center mb-8">
            <img src="/fulllogo.png" alt="CoTune" className="h-10 w-auto mx-auto mb-4" />
          </div>

          {/* Name Entry Card */}
          <div className="card p-8">
            <button
              onClick={handleBack}
              className="flex items-center text-content-secondary hover:text-content mb-6 transition-colors"
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              Back
            </button>

            <div className="text-center mb-6">
              <div className={`inline-flex items-center justify-center w-16 h-16 rounded-full mb-4 ${
                selectedType === 'artist' 
                  ? 'bg-gradient-to-br from-pink-500 to-purple-600' 
                  : 'bg-gradient-to-br from-blue-500 to-indigo-600'
              }`}>
                {selectedType === 'artist' ? (
                  <svg className="w-8 h-8 text-content" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3" />
                  </svg>
                ) : (
                  <svg className="w-8 h-8 text-content" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                )}
              </div>
              <h2 className="text-2xl font-bold text-content mb-2">
                {selectedType === 'artist' ? 'Artist Account' : 'Manager Account'}
              </h2>
              <p className="text-content-secondary">
                {selectedType === 'artist' 
                  ? 'Enter your artist or project name'
                  : 'Enter your management company name'}
              </p>
            </div>

            {error && (
              <div className="mb-6 p-4 bg-red-500/20 border border-red-500/40 rounded-lg text-red-300">
                {error}
              </div>
            )}

            <div className="space-y-4">
              <div>
                <label htmlFor="accountName" className="block text-sm font-medium text-content-secondary mb-2">
                  Account Name
                </label>
                <input
                  type="text"
                  id="accountName"
                  value={accountName}
                  onChange={(e) => setAccountName(e.target.value)}
                  placeholder={selectedType === 'artist' ? 'e.g., John Smith' : 'e.g., Acme Music Management'}
                  className="w-full px-4 py-3 bg-surface-tertiary border border-border-subtle rounded-lg text-content placeholder-content-muted focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent"
                  autoFocus
                />
              </div>

              {selectedType === 'artist' && (
                <>
                  <div>
                    <label htmlFor="ipiNumber" className="block text-sm font-medium text-content-secondary mb-2">
                      IPI Number <span className="text-content-muted">(optional)</span>
                    </label>
                    <input
                      type="text"
                      id="ipiNumber"
                      value={ipiNumber}
                      onChange={(e) => setIpiNumber(e.target.value)}
                      placeholder="e.g., 00123456789"
                      className="w-full px-4 py-3 bg-surface-tertiary border border-border-subtle rounded-lg text-content placeholder-content-muted focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent"
                    />
                    <p className="mt-1 text-xs text-content-muted">Your Interested Parties Information number from your PRO</p>
                  </div>

                  <div>
                    <label htmlFor="pro" className="block text-sm font-medium text-content-secondary mb-2">
                      PRO <span className="text-content-muted">(optional)</span>
                    </label>
                    <select
                      id="pro"
                      value={pro}
                      onChange={(e) => setPro(e.target.value)}
                      className="w-full px-4 py-3 bg-surface-tertiary border border-border-subtle rounded-lg text-content focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent"
                    >
                      <option value="">Select your PRO</option>
                      <option value="ASCAP">ASCAP</option>
                      <option value="BMI">BMI</option>
                      <option value="SESAC">SESAC</option>
                      <option value="GMR">GMR</option>
                      <option value="PRS">PRS</option>
                      <option value="SOCAN">SOCAN</option>
                      <option value="APRA AMCOS">APRA AMCOS</option>
                      <option value="GEMA">GEMA</option>
                      <option value="SACEM">SACEM</option>
                      <option value="Other">Other</option>
                    </select>
                    <p className="mt-1 text-xs text-content-muted">Your Performing Rights Organization</p>
                  </div>
                </>
              )}

              <PrimaryButton
                onClick={handleCreateAccount}
                disabled={isLoading || !accountName.trim()}
                loading={isLoading}
                className="w-full"
              >
                Create Account
              </PrimaryButton>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Step 1: Select account type
  return (
    <div className="min-h-screen bg-gradient-secondary flex items-center justify-center px-4">
      <div className="max-w-2xl w-full">
        {/* Logo */}
        <div className="text-center mb-8">
          <img src="/fulllogo.png" alt="CoTune" className="h-10 w-auto mx-auto mb-4" />
          <p className="text-[#14B8A6]">How are you using CoTune?</p>
        </div>

        {/* Welcome Card */}
        <div className="card p-8 text-center">

          {error && (
            <div className="mb-6 p-4 bg-red-500/20 border border-red-500/40 rounded-lg text-red-300">
              {error}
            </div>
          )}

          {/* Choice Cards */}
          <div className="grid md:grid-cols-2 gap-6">
            {/* Artist Option */}
            <button
              onClick={() => handleSelectType('artist')}
              disabled={isLoading}
              className="group p-6 rounded-xl border-2 border-border-subtle bg-surface-secondary/50 hover:border-accent hover:bg-accent/10 transition-all duration-200 text-left disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <div className="flex items-center justify-center w-16 h-16 rounded-full bg-[#14B8A6] mb-4 mx-auto group-hover:scale-110 transition-transform">
                <svg className="w-8 h-8 text-content" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-content mb-2 text-center">
                I'm an Artist
              </h3>
              <p className="text-content-secondary text-sm text-center">
                Create and manage splits for your own music. Track who wrote what and get everyone on the same page.
              </p>
            </button>

            {/* Manager Option */}
            <button
              onClick={() => handleSelectType('manager')}
              disabled={isLoading}
              className="group p-6 rounded-xl border-2 border-border-subtle bg-surface-secondary/50 hover:border-accent hover:bg-accent/10 transition-all duration-200 text-left disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <div className="flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 mb-4 mx-auto group-hover:scale-110 transition-transform">
                <svg className="w-8 h-8 text-content" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-content mb-2 text-center">
                I Manage Artists
              </h3>
              <p className="text-content-secondary text-sm text-center">
                Oversee multiple artists and their works. See everything across your roster at a glance.
              </p>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AccountSetup;
